import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useVoiceOutput = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioQueueRef = useRef<string[]>([]);
  const isProcessingQueueRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (isProcessingQueueRef.current || audioQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    const audioBase64 = audioQueueRef.current.shift();

    if (!audioBase64) {
      isProcessingQueueRef.current = false;
      return;
    }

    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Convert base64 to blob
      const binaryString = atob(audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);

      // Create and play audio
      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onplay = () => setIsSpeaking(true);
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
        isProcessingQueueRef.current = false;
        processQueue(); // Process next item
      };
      audio.onerror = (error) => {
        console.error('Audio playback error:', error);
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
        isProcessingQueueRef.current = false;
        processQueue(); // Try next item
      };

      await audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsSpeaking(false);
      isProcessingQueueRef.current = false;
      processQueue(); // Try next item
    }
  }, []);

  const speak = useCallback(async (text: string) => {
    if (!text || text.trim().length === 0) return;

    setIsGenerating(true);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: { text: text.trim() }
      });

      if (error) {
        console.error('TTS error:', error);
        throw error;
      }

      if (data?.audioContent) {
        audioQueueRef.current.push(data.audioContent);
        await processQueue();
      }
    } catch (error) {
      console.error('Failed to generate speech:', error);
      toast.error('Failed to generate voice. Check your connection.');
    } finally {
      setIsGenerating(false);
    }
  }, [processQueue]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    audioQueueRef.current = [];
    isProcessingQueueRef.current = false;
    setIsSpeaking(false);
    setIsGenerating(false);
  }, []);

  return {
    speak,
    stop,
    isSpeaking,
    isGenerating
  };
};
